import EmailIcon from "@redotech/redo-icons/mail-02.svg";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import type {
  BackInStockProduct,
  BackInStockSubscriber,
} from "@redotech/redo-model/customer-product-notification/customer-product-notification";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  RedoTable,
  RedoTableColumn,
  RedoTableLoadingStateContent,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import * as styles from "./marketing-analytics-back-in-stock.module.css";
import { UploadBisModal } from "./upload-bis-modal";

const ProductListItem = memo(function ProductListItem({
  product,
  isSelected,
  onClick,
}: {
  product: BackInStockProduct;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      bgColor={isSelected ? "secondary" : "primary"}
      borderColor={isSelected ? "brand" : "primary"}
      borderWidth="1px"
      cursor="pointer"
      dir="column"
      gap="sm"
      onClick={onClick}
      p="lg"
      radius="lg"
    >
      <Flex align="center" gap="md">
        <Flex
          align="center"
          bgColor="tertiary"
          className={styles.imageContainer}
          justify="center"
          radius="md"
        >
          {product.imageUrl ? (
            <img
              alt={product.name}
              src={product.imageUrl}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          ) : (
            <Text fontSize="xs" textColor="secondary">
              [img]
            </Text>
          )}
        </Flex>
        <Flex dir="column" flex="1" gap="xs">
          <Text fontSize="sm" fontWeight="medium" textColor="primary">
            {product.name}
          </Text>
          {product.status === "out_of_stock" && (
            <RedoBadge text="Out of stock" />
          )}
          <Flex gap="sm">
            <Text fontSize="xs" textColor="secondary">
              {product.waitingCount} waiting
            </Text>
            {product.notifiedCount > 0 && (
              <Text fontSize="xs" textColor="secondary">
                • {product.notifiedCount} notified
              </Text>
            )}
          </Flex>
          <Text fontSize="sm" fontWeight="medium" textColor="primary">
            ${product.potentialRevenue.toLocaleString()}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});

const ProductDetailView = memo(function ProductDetailView({
  product,
  productId,
  variantId,
  refetchAllProducts,
}: {
  product: BackInStockProduct;
  productId: string;
  variantId: string;
  refetchAllProducts: () => void;
}) {
  const [filterStatus, setFilterStatus] = useState<
    "all" | "waiting" | "notified"
  >("all");
  // const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(
  //   new Set(),
  // );

  // Modal state for manual notification trigger
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [oldInventory, setOldInventory] = useState<number>(0);
  const [newInventory, setNewInventory] = useState<number>(10);
  const [validationError, setValidationError] = useState<string>();

  const {
    data: subscribersData,
    isLoading,
    refetch: refetchSubscribers,
  } = useRpcQuery(RedoMarketingRpcClientContext, {
    method: "getBackInStockSubscribers",
    args: { productId, variantId, status: filterStatus },
  });

  const notifyMutation = useRpcMutation(RedoMarketingRpcClientContext, {
    method: "triggerBackInStockNotifications",
  });

  const subscribers = subscribersData?.subscribers || [];
  const totalWaiting = subscribers.filter((s) => s.status === "waiting").length;
  const totalNotified = subscribers.filter(
    (s) => s.status === "notified",
  ).length;

  // Validation
  const validateInventoryNumbers = () => {
    if (newInventory <= oldInventory) {
      setValidationError(
        "New inventory must be greater than previous inventory",
      );
      return false;
    }
    setValidationError(undefined);
    return true;
  };

  // Handler
  const handleNotifyAll = async () => {
    if (!validateInventoryNumbers()) return;

    await alertOnFailure("Failed to trigger notifications")(async () => {
      const result = await notifyMutation.mutateAsync({
        productId,
        variantId,
        oldInventoryCount: oldInventory,
        newInventoryCount: newInventory,
      });

      toast(
        `Successfully triggered ${result.notificationCount} notification${result.notificationCount === 1 ? "" : "s"}`,
        { variant: "success" },
      );
      setIsNotifyModalOpen(false);
      void refetchAllProducts();
      void refetchSubscribers();
    });
  };

  return (
    <Flex dir="column" flex="1" gap="xl" overflow="hidden" py="xl">
      <Flex dir="column" gap="lg" px="xl">
        <Text fontSize="xl" fontWeight="semibold" textColor="primary">
          {product.name}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          SKU: {product.sku}
        </Text>

        <Flex
          align="center"
          bgColor="tertiary"
          height="quarter-screen"
          justify="center"
          radius="lg"
          w="full"
        >
          {product.imageUrl ? (
            <img
              alt={product.name}
              src={product.imageUrl}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Text fontSize="sm" textColor="secondary">
              [Product Image]
            </Text>
          )}
        </Flex>
      </Flex>

      {/* <Flex dir="column" gap="md">
        <Text fontSize="md" fontWeight="medium" textColor="primary">
          Demand Metrics
        </Text>
        <Flex gap="md" wrap="wrap">
          <RedoMetricItem
            title="Waiting"
            value={product.waitingCount.toString()}
            description={`${product.notifiedCount} notified`}
            comparisonChange={
              product.conversionRate > 15
                ? product.conversionRate
                : -product.conversionRate
            }
            comparisonText="convert"
            isGoodChange={product.conversionRate > 15}
          />
          <RedoMetricItem
            title="Potential Revenue"
            value={`$${product.potentialRevenue.toLocaleString()}`}
            description={`${product.openRate}% open`}
          />
          <RedoMetricItem
            title="Avg Wait Time"
            value={`${product.avgWaitDays} days`}
            description={`${product.ctr}% CTR`}
          />
        </Flex>
      </Flex> */}

      <Flex dir="column" flex="1" gap="lg" overflow="hidden">
        <Flex dir="column" gap="none" px="xl">
          <Text fontSize="lg" fontWeight="medium" textColor="primary">
            Subscribers:
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {subscribersData?.totalCount || 0} subscriber
            {(subscribersData?.totalCount || 0) !== 1 ? "s" : ""} •{" "}
            {totalWaiting} waiting • {totalNotified} notified
          </Text>
        </Flex>

        <Flex align="center" gap="md" justify="space-between" px="xl">
          <div className={styles.dropdownContainer}>
            <RedoSingleSelectDropdownInput
              options={[
                { id: "all", type: "text", text: "All", value: "all" },
                {
                  id: "waiting",
                  type: "text",
                  text: "Waiting",
                  value: "waiting",
                },
                {
                  id: "notified",
                  type: "text",
                  text: "Notified",
                  value: "notified",
                },
              ]}
              optionSelected={({ item }) =>
                setFilterStatus(item.value as "all" | "waiting" | "notified")
              }
              placeholder="Select status"
              selectedItem={{
                id: filterStatus,
                text:
                  filterStatus === "all"
                    ? "All"
                    : filterStatus === "waiting"
                      ? "Waiting"
                      : "Notified",
                type: "text",
                value: filterStatus,
              }}
            />
          </div>
          <Flex align="center" gap="md" px="xl">
            <RedoButton
              disabled={totalWaiting === 0 || notifyMutation.isPending}
              hierarchy="primary"
              onClick={() => setIsNotifyModalOpen(true)}
              pending={notifyMutation.isPending}
              size="sm"
              text="Notify all waiting"
            />
          </Flex>
        </Flex>

        <Flex dir="column" flex={1} gap="none" overflow="auto">
          <RedoTable<BackInStockSubscriber>
            emptyStateContent={
              !isLoading && subscribers.length === 0 ? (
                <Flex align="center" flex={1} justify="center">
                  <RedoEmptyState
                    Icon={EmailIcon}
                    subtext="Subscribers will appear here when customers sign up for back in stock notifications."
                    text="No subscribers found"
                  />
                </Flex>
              ) : undefined
            }
            layout={useMemo(() => {
              const columns: RedoTableColumn<BackInStockSubscriber>[] = [
                // {
                //   widthMode: { type: "fixed", width: 48 },
                //   renderHeaderCell: () => (
                //     <Flex align="center" justify="center" p="md">
                //       <RedoCheckbox
                //         value={
                //           selectedSubscribers.size === subscribers.length &&
                //           subscribers.length > 0
                //         }
                //         setValue={(checked) => {
                //           if (checked) {
                //             setSelectedSubscribers(
                //               new Set(subscribers.map((s) => s.id)),
                //             );
                //           } else {
                //             setSelectedSubscribers(new Set());
                //           }
                //         }}
                //       />
                //     </Flex>
                //   ),
                //   renderNormalCell: ({ row }) => (
                //     <Flex align="center" justify="center" p="md">
                //       <RedoCheckbox
                //         value={selectedSubscribers.has(row.id)}
                //         setValue={(checked) => {
                //           const newSelected = new Set(selectedSubscribers);
                //           if (checked) {
                //             newSelected.add(row.id);
                //           } else {
                //             newSelected.delete(row.id);
                //           }
                //           setSelectedSubscribers(newSelected);
                //         }}
                //       />
                //     </Flex>
                //   ),
                // },
                {
                  widthMode: { type: "fill" },
                  renderHeaderCell: () =>
                    StandardRedoTableHeaderCell({
                      headerText: "Email / Phone",
                    }),
                  renderNormalCell: ({ row }) => {
                    const parts = [row.email, row.phone].filter(Boolean);
                    return <RedoTableTextCell text={parts.join(" - ")} />;
                  },
                },
                {
                  widthMode: { type: "fill" },
                  renderHeaderCell: () =>
                    StandardRedoTableHeaderCell({
                      headerText: "Subscribe Date",
                    }),
                  renderNormalCell: ({ row }) => (
                    <RedoTableTextCell
                      text={row.subscribedDate.toLocaleDateString()}
                    />
                  ),
                },
                {
                  widthMode: { type: "fill" },
                  renderHeaderCell: () =>
                    StandardRedoTableHeaderCell({ headerText: "Status" }),
                  renderNormalCell: ({ row }) => (
                    <Flex p="xl">
                      <RedoBadge
                        color={row.status === "waiting" ? "warning" : "success"}
                        text={
                          row.status === "waiting"
                            ? `Waiting ${row.waitingDays} day${row.waitingDays !== 1 ? "s" : ""}`
                            : `Notified ${row.notifiedDate?.toLocaleDateString()}`
                        }
                      />
                    </Flex>
                  ),
                },
              ];
              return { columns, headerColor: "white" as const };
            }, [])}
            loadingStateContent={
              isLoading ? (
                <Flex align="center" justify="center">
                  <RedoTableLoadingStateContent />
                </Flex>
              ) : undefined
            }
            rowsOfData={subscribers}
            size="sm"
          />

          <Divider />

          <Flex align="center" justify="space-between" pt="md" px="xl">
            <Text fontSize="sm" textColor="secondary">
              Showing {subscribers.length} of {subscribersData?.totalCount || 0}
            </Text>
          </Flex>
        </Flex>

        {/* <Flex gap="md" pt="lg">
          <RedoButton size="sm" hierarchy="secondary" text="Export List" />
          <RedoButton size="sm" hierarchy="secondary" text="Create Segment" />
          <RedoButton size="sm" hierarchy="primary" text="Notify Now" />
        </Flex> */}
      </Flex>

      <RedoModal
        isOpen={isNotifyModalOpen}
        modalSize="md"
        onModalCloseRequested={() => {
          setIsNotifyModalOpen(false);
          setValidationError(undefined);
        }}
        primaryButton={{
          text: "Confirm & Send",
          onClick: handleNotifyAll,
          disabled:
            newInventory == null ||
            oldInventory == null ||
            notifyMutation.isPending,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setIsNotifyModalOpen(false),
        }}
        subtitle={`Notify ${totalWaiting} waiting subscriber${totalWaiting === 1 ? "" : "s"}`}
        title="Notify all waiting subscribers"
      >
        <Flex dir="column" gap="lg">
          <Flex align="center" dir="row" gap="md" justify="space-around">
            <RedoIncrementDecrement
              label="Previous inventory"
              min={0}
              placeholder="0"
              setValue={(value) => setOldInventory(value)}
              value={oldInventory}
            />

            <RedoIncrementDecrement
              label="New inventory"
              min={1}
              placeholder="10"
              setValue={(value) => setNewInventory(value)}
              value={newInventory}
            />
          </Flex>

          <Text fontSize="sm" textColor="secondary">
            These inventory numbers determine which BIS flows will trigger based
            on their minimum inventory threshold settings. They will also be
            used to populate the corresponding variables in flow conditions and
            email/SMS templates.
          </Text>
          {validationError && (
            <Text fontSize="sm" textColor="error">
              {validationError}
            </Text>
          )}
        </Flex>
      </RedoModal>
    </Flex>
  );
});

export const MarketingAnalyticsBackInStock = memo(
  function MarketingAnalyticsBackInStock() {
    const [searchQuery, setSearchQuery] = useState("");
    const debouncedSearch = useDebounce(searchQuery, 500);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(
      null,
    );
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const {
      data: productsData,
      isLoading,
      refetch,
    } = useRpcQuery(RedoMarketingRpcClientContext, {
      method: "getBackInStockProducts",
      args: { limit: 50, searchQuery: debouncedSearch || undefined },
    });

    const products = useMemo(
      () => productsData?.products || [],
      [productsData?.products],
    );
    const selectedProduct =
      products.find((p) => p.id === selectedProductId) || products[0];

    useEffect(() => {
      if (!selectedProductId && products.length > 0) {
        setSelectedProductId(products[0].id);
      }
    }, [products, selectedProductId]);

    return (
      <Flex dir="row" gap="none" height="full" w="full">
        <Flex
          bgColor="primary"
          borderColor="primary"
          borderRightWidth="1px"
          borderStyle="solid"
          bottomLeftRadius="lg"
          dir="column"
          flex={1}
          gap="none"
          overflow="hidden"
          pt="2xl"
          topLeftRadius="lg"
        >
          <Flex dir="column" gap="md" pb="2xl" pl="2xl" pr="4xl">
            <Flex align="center" justify="space-between">
              <Text fontSize="md" fontWeight="semibold" textColor="primary">
                Products
              </Text>
              <RedoButton
                hierarchy="secondary"
                IconLeading={UploadIcon}
                onClick={() => setUploadModalOpen(true)}
                size="xs"
                text="Upload"
              />
            </Flex>
            <RedoTextInput
              placeholder="Search products..."
              setValue={setSearchQuery}
              size="sm"
              value={searchQuery}
            />
          </Flex>

          <Divider />

          {isLoading ? (
            <Flex align="center" height="quarter-screen" justify="center">
              <RedoTableLoadingStateContent />
            </Flex>
          ) : products.length === 0 ? (
            <RedoEmptyState
              Icon={EmailIcon}
              subtext="Products with waiting subscribers will appear here."
              text="No back in stock products"
            />
          ) : (
            <Flex dir="column" flex={1} overflow="auto" p="2xl">
              {products.map((product) => (
                <ProductListItem
                  isSelected={product.id === selectedProductId}
                  key={product.id}
                  onClick={() => setSelectedProductId(product.id)}
                  product={product}
                />
              ))}
            </Flex>
          )}
        </Flex>

        <Flex
          bgColor="primary"
          bottomRightRadius="lg"
          flex={3}
          overflow="hidden"
          topRightRadius="lg"
        >
          {selectedProduct ? (
            <ProductDetailView
              product={selectedProduct}
              productId={selectedProduct.productId}
              refetchAllProducts={refetch}
              variantId={selectedProduct.variantId}
            />
          ) : (
            <Flex align="center" height="full" justify="center" w="full">
              <RedoEmptyState
                Icon={EmailIcon}
                subtext="Choose a product from the list to view details."
                text="Select a product"
              />
            </Flex>
          )}
        </Flex>

        <UploadBisModal
          onClose={() => setUploadModalOpen(false)}
          onUploadComplete={() => refetch()}
          open={uploadModalOpen}
        />
      </Flex>
    );
  },
);
